import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './app.vue';
import router from './router/router';
import * as config from './shared/config/config';
import UserManagementService from '@/venit/admin/user-management/user-management.service';
import LoginService from './account/login.service';
import AccountService from './account/account.service';
import DictService from '@/venit/services/dict/dict.service';
import TranslationService from '@/locale/translation.service';
import vuetify from './plugins/vuetify';
import UploadsManagementService from '@/venit/admin/uploads-management/uploads-management.service';
import UploadService from '@/venit/services/upload/upload.service';
import ProductsManagementService from './venit/admin/products-management/products-management.service';
import FiltersManagementService from './venit/admin/filters-management/filters-management.service';
import MenuManagementService from '@/venit/admin/menu-management/menu-management.service';
import ResourceManagementService from './venit/admin/resource-management/resource-management.service';
import ResourcePropertiesService from '@/venit/services/api/resource-properties/resource-properties-dict.service';

/** public api services */
import ApiMenuService from './venit/services/api/menu/menu.service';
import ApiProductService from './venit/services/api/product/product.service';
import OrdersService from './venit/admin/orders-management/orders-service';
import PartsManagementService from '@/venit/admin/parts-management/parts-management.service';
import OrganizationsService from '@/venit/admin/organizations-management/organizations-management.service';
import ResourcePermissionsDictService from './venit/services/api/resource-permissions/resource-permissions-dict.service';
import UserResourcePermissionGroupsService from '@/venit/services/api/resource-permissions/user-resource-permission-groups.service';
import UserCartService from '@/venit/services/cart/user-cart.service';
import AdminCartsService from '@/venit/admin/carts-management/carts-service';
import ValidationService from '@/locale/validation.service';
import UserProfileService from '@/venit/user/profile/user-profile.service';
import UserOrdersService from '@/venit/user/orders/user-orders.service';
import ApiSearchService from '@/venit/services/search/search.service';
import CorrespondenceManagementService from '@/venit/admin/correspondence-management/correspondence-management.service';
import ImportPropertyMappingService from '@/venit/services/api/dict/import-property-mapping.service';
import ImportAutoMatchConfigService from '@/venit/services/api/dict/import-auto-match-config.service';

// Compat mode disabled - using Vue 3 native mode
// configureCompat({ MODE: 3 });

const pinia = createPinia();
const i18n = config.initI18N();

const app = createApp(App);

// konfiguracja potrzebna dla presentera venit
app.config.globalProperties.$ininCache = {};
app.config.globalProperties.$window = window;

config.initVueApp(app);

app.use(pinia);
app.use(router);
app.use(i18n);
app.use(vuetify);

// Create service instances
const dictService = new DictService();
const translationService = new TranslationService(i18n);
const accountService = new AccountService(translationService, dictService, null, router);
const loginService = new LoginService(accountService, router);
const menuService = new ApiMenuService();
const apiProductService = new ApiProductService();
const ordersService = new OrdersService();
const partsManagementService = new PartsManagementService();
const resourcePropertiesService = new ResourcePropertiesService();
const resourcePermissionsDictService = new ResourcePermissionsDictService();
const userResourcePermissionGroupsService = new UserResourcePermissionGroupsService();
const userCartService: UserCartService = new UserCartService();
const adminCartsService: AdminCartsService = new AdminCartsService();
const validationService = new ValidationService(i18n);

// Provide services
app.provide('loginService', () => loginService);
app.provide('userService', () => new UserManagementService());
app.provide('organizationsService', () => new OrganizationsService());
app.provide('resourceService', () => new ResourceManagementService());
app.provide('uploadsService', () => new UploadsManagementService());
app.provide('productsService', () => new ProductsManagementService());
app.provide('filtersService', () => new FiltersManagementService());
app.provide('uploadService', () => new UploadService());
app.provide('menuManagementService', () => new MenuManagementService());
app.provide('validationService', () => validationService);
app.provide('translationService', () => translationService);
app.provide('accountService', () => accountService);
app.provide('menuService', () => menuService);
app.provide('dictService', () => dictService);
app.provide('apiProductService', () => apiProductService);
app.provide('ordersService', () => ordersService);
app.provide('partsManagementService', () => partsManagementService);
app.provide('resourcePropertiesService', () => resourcePropertiesService);
app.provide('resourcePermissionsDictService', () => resourcePermissionsDictService);
app.provide('userResourcePermissionGroupsService', () => userResourcePermissionGroupsService);
app.provide('userCartService', () => userCartService);
app.provide('adminCartsService', () => adminCartsService);
app.provide('userProfileService', () => new UserProfileService());
app.provide('userOrdersService', () => new UserOrdersService());
app.provide('searchService', () => new ApiSearchService());
app.provide('correspondenceService', () => new CorrespondenceManagementService());
app.provide('importPropertyMappingService', () => new ImportPropertyMappingService());
app.provide('importAutoMatchConfigService', () => new ImportAutoMatchConfigService());

// Router guards
router.beforeEach((to, from, next) => {
  if (!to.matched.length) {
    next('/not-found');
    return;
  }

  if (to.meta && to.meta.authorities && (to.meta.authorities as string[]).length > 0) {
    accountService.hasAnyAuthorityAndCheckAuth(to.meta.authorities as string[]).then(value => {
      if (!value) {
        sessionStorage.setItem('requested-url', to.fullPath);
        next('/forbidden');
      } else {
        next();
      }
    });
  } else {
    next();
  }
});

app.mount('#app');
